import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Search, Lock, Gift, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

/**
 * Toolbox = the user's personal library of claimed/purchased items.
 * It is NOT a storefront. New items are discovered via /free-resources
 * (free library) or /courses (course + paid toolkit catalog).
 */
export default function Toolbox() {
  const [data, setData] = useState({ items: [], free_count: 0, paid_count: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | free | paid
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/me/toolbox")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load your toolbox."))
      .finally(() => setLoading(false));
  }, [user]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data.items || []).filter((p) => {
      const isFree = p.tier === "free" || !p.price;
      if (filter === "free" && !isFree) return false;
      if (filter === "paid" && isFree) return false;
      if (s && !`${p.name} ${p.description || ""}`.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [data.items, filter, q]);

  if (!user) {
    return (
      <div className="bg-brand-alabaster min-h-screen">
        <Navbar />
        <section className="py-24 max-w-3xl mx-auto px-6 text-center">
          <Lock className="w-10 h-10 mx-auto text-brand-pink mb-4" />
          <h1 className="font-display text-4xl md:text-5xl mb-4">Your Toolbox is private.</h1>
          <p className="text-neutral-600 mb-8">Log in to see every checklist, template, and bundle you've claimed or purchased.</p>
          <div className="flex gap-3 justify-center">
            <Button data-testid="toolbox-login-cta" onClick={() => nav("/login")} className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white px-6">Log in</Button>
            <Link to="/free-resources"><Button data-testid="toolbox-browse-free-cta" variant="outline" className="rounded-full border-neutral-300">Browse free resources</Button></Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-brand-alabaster min-h-screen">
      <Navbar />
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Your Toolbox</div>
        <h1 className="font-display text-5xl md:text-6xl max-w-3xl leading-tight">
          Everything you've <span className="italic text-brand-pink">claimed & bought</span>.
        </h1>
        <p className="mt-6 text-neutral-600 max-w-2xl text-lg">
          Your personal library — free checklists you've grabbed and paid toolkits you own. Download anything, anytime.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          {[
            { id: "all", label: `All (${data.items.length})` },
            { id: "free", label: `Free (${data.free_count})` },
            { id: "paid", label: `Paid (${data.paid_count})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              data-testid={`toolbox-filter-${t.id}`}
              className={`px-4 py-1.5 rounded-full text-xs transition ${
                filter === t.id
                  ? "bg-brand-pink text-white"
                  : "bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {t.label}
            </button>
          ))}
          <div className="relative flex-1 min-w-[220px] ml-auto">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              data-testid="toolbox-search"
              placeholder="Search your library…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-full h-11 pl-11 border-neutral-200 bg-white"
            />
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="text-neutral-500 py-16 text-center">Loading your library…</div>
          ) : filtered.length === 0 ? (
            <EmptyState hasItems={data.items.length > 0} />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((p) => (
                <ToolboxCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

function EmptyState({ hasItems }) {
  return (
    <div className="rounded-3xl bg-white border border-neutral-100 py-16 px-6 text-center">
      <Gift className="w-10 h-10 mx-auto text-brand-pink mb-4" />
      <h3 className="font-display text-2xl mb-2">{hasItems ? "No items match that filter." : "Your toolbox is empty — for now."}</h3>
      <p className="text-neutral-600 max-w-md mx-auto mb-6">
        {hasItems
          ? "Try switching filters or clearing your search."
          : "Grab a free checklist or pick up a paid toolkit — everything you own will land here."}
      </p>
      <div className="flex gap-3 justify-center">
        <Link to="/free-resources">
          <Button data-testid="toolbox-empty-free-cta" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white px-5">
            <Gift className="w-4 h-4 mr-1.5" /> Grab free resources
          </Button>
        </Link>
        <Link to="/courses">
          <Button data-testid="toolbox-empty-catalog-cta" variant="outline" className="rounded-full border-neutral-300 px-5">
            <ShoppingBag className="w-4 h-4 mr-1.5" /> Browse the catalog
          </Button>
        </Link>
      </div>
    </div>
  );
}

function ToolboxCard({ product }) {
  const isFree = product.tier === "free" || !product.price;
  const href = product.download_url && product.download_url !== "#" ? product.download_url : null;

  return (
    <article
      data-testid={`toolbox-item-${product.id}`}
      className="rounded-3xl bg-white border border-neutral-100 p-6 hover:shadow-sm transition"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider mb-3">
        <span className={`px-2 py-0.5 rounded-full ${isFree ? "bg-brand-pink/10 text-brand-pink" : "bg-black text-white"}`}>
          {isFree ? "Free" : `Paid · $${product.price}`}
        </span>
        <span className="text-neutral-500">{product.category || "General"}</span>
      </div>
      <h3 className="font-display text-xl leading-tight mb-2">{product.name}</h3>
      <p className="text-sm text-neutral-600 mb-5 line-clamp-3">{product.description}</p>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          data-testid={`toolbox-download-${product.id}`}
          className="inline-flex items-center justify-center h-9 px-4 rounded-full bg-brand-pink text-white text-xs hover:bg-brand-pink/90 transition"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" /> Download
        </a>
      ) : (
        <span className="inline-flex items-center h-9 px-4 rounded-full bg-neutral-100 text-neutral-500 text-xs">
          File coming soon
        </span>
      )}
    </article>
  );
}
